import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import api from '../config/api';
import { DOCUMENT } from '../constants/documentTheme';
import { partnerLinkSx, partnerOxbloodBtnSx } from './PartnerListChrome';
import { buildPartnerRecommendationDescription } from '../utils/partnerRecommendation';

const CATEGORIES = ['restaurant', 'activity', 'transport', 'hotel', 'event', 'other'];

const PartnerRecommendDialog = ({
  open,
  onClose,
  trip,
  seed = null,
  onSent,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState('activity');
  const [startDatetime, setStartDatetime] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setName(seed?.name || '');
    setLocation(seed?.location || '');
    setCategory(seed?.category || 'activity');
    setPrice(seed?.cost != null ? String(seed.cost) : '');
    setCurrency(seed?.cost_currency || 'USD');
    setNotes('');
    const start = trip?.start_date
      ? `${String(trip.start_date).slice(0, 10)}T12:00`
      : '';
    setStartDatetime(seed?.start_datetime || start);
  }, [open, seed, trip]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!trip?.id) return;
    if (!name.trim() || !location.trim() || !startDatetime) {
      setError(t('partner.opsHome.recommendRequired', 'Name, location, and start time are required.'));
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const description = buildPartnerRecommendationDescription({
        price,
        currency,
        notes,
        managerLabel: t('partner.opsHome.recommendByline', 'Recommended by your lifestyle manager'),
      });
      const payload = {
        name: name.trim(),
        location: location.trim(),
        category,
        start_datetime: startDatetime.length === 16 ? `${startDatetime}:00` : startDatetime,
        description,
      };
      await api.post(`/trips/${trip.id}/suggestions`, payload, { usePartnerAuth: true });
      enqueueSnackbar(t('partner.opsHome.recommendSent'), { variant: 'success' });
      onSent?.();
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || t('partner.opsHome.recommendFailed', 'Could not send recommendation.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-testid="partner-recommend-dialog">
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontFamily: DOCUMENT.display }}>
          {t('partner.opsHome.recommendTitle')}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2 }}>
            {t('partner.opsHome.recommendHint')}
          </Typography>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('itinerary.name', 'Name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            inputProps={{ 'data-testid': 'partner-recommend-name' }}
          />
          <TextField
            label={t('itinerary.location', 'Location')}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            required
            fullWidth
          />
          <TextField
            select
            label={t('itinerary.category', 'Category')}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            fullWidth
          >
            {CATEGORIES.map((value) => (
              <MenuItem key={value} value={value}>{value}</MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('itinerary.start', 'Start')}
            type="datetime-local"
            value={startDatetime}
            onChange={(e) => setStartDatetime(e.target.value)}
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={t('partner.opsHome.recommendPrice')}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            type="number"
            fullWidth
            inputProps={{ min: 0, step: '0.01', 'data-testid': 'partner-recommend-price' }}
          />
          <TextField
            select
            label={t('partner.opsHome.recommendCurrency', 'Currency')}
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            fullWidth
          >
            {['USD', 'EUR', 'GBP', 'AED'].map((code) => (
              <MenuItem key={code} value={code}>{code}</MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('partner.opsHome.recommendNotes', 'Notes for client')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button type="button" onClick={onClose} sx={partnerLinkSx}>
            {t('partnerLists.cancel')}
          </Button>
          <Button type="submit" disabled={submitting} sx={partnerOxbloodBtnSx} data-testid="partner-recommend-submit">
            {submitting ? t('partnerManage.sending') : t('partner.opsHome.recommendSend')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default PartnerRecommendDialog;
